import { CopyOutlined } from '@ant-design/icons';
import { Alert, Heading, Line, Link, TooltipHeading } from '@mm/components';
import { QuestionCircleIcon } from '@mm/icons';
import { useChatbotCRUDActions } from '@mm/state/actions/ChatbotCRUDActions';
import { useDomainCRUDActions } from '@mm/state/actions/DomainCRUDActions';
import { usePromoterCRUDActions } from '@mm/state/actions/PromoterCRUDActions';
import { useViewActions } from '@mm/state/actions/viewActions';
import { PromoterModel, PromoterTypes } from '@mm/state/model/PromoterModel';
import { domainSelector } from '@mm/state/selectors/domainSelector';
import { activeFacebookPageSelector } from '@mm/state/selectors/facebookPageSelector';
import { activeChatbotSelector } from '@mm/state/selectors/viewSelectors';
import {
  isKnownSiteType,
  KnownSiteType,
  KNOWN_SITE_TYPES,
} from '@mm/stateful-components/ChatWidget/InstallationGuide/constants';
import { notification } from 'antd';
import { ReactComponent as GreenCheckCircle } from 'assets/icons/green-check-circle.svg';
import classNames from 'classnames';
import React, { useEffect, useState } from 'react';
import CopyToClipboard from 'react-copy-to-clipboard';
import { useSelector } from 'react-redux';
import { PrimaryButton } from '../../../../../../common/buttons/PrimaryButton/PrimaryButton';
import { GreenMarker } from '../../../../../../common/components/GreenMarker/GreenMarker';
import TextInput from '../../../../../../common/components/TextInput';
import { useGreenCueMarkers } from '../../../../../../common/hooks/use-green-cue-markers';
import { BotBuilderRoutes } from '../../../../../../routes/bot-builder-routes';
import { colors } from '../../../../beta/styles/colors';
import { getConnectionStatus } from '../../../../configure/panels/sections/MaintenanceSettingsSection/FacebookConnectionStatus/FacebookConnectionStatus';
import {
  useCustomPromoterJSCodes,
  usePromoterJSCode,
} from '../../../common/use-promoter-js-code';
import { CHAT_WIDGET_CHANNEL_DESCRIPTIONS } from '../shared';
import './CustomerChatInstallInformation.pcss';
import dudaIcon from './duda.png';
import gtmIcon from './gtm.png';
import leadpagesIcon from './leadpages.png';
import shopifyIcon from './shopify.png';
import squarespaceIcon from './squarespace.png';
import weeblyIcon from './weebly.png';
import wixIcon from './wix.png';
import wordpressIcon from './wordpress.png';
import bigCommerce from 'assets/big-commerce.svg';

type Props = {
  promoter: PromoterModel;
  setShowGreenCueForActivate?: (value: boolean) => void;
  usedInSlideout?: boolean;
  hideWhitelist?: boolean;
};

const SITE_TYPE_DEFINITIONS: Record<
  KnownSiteType,
  {
    iconUrl: string;
    shortName?: string;
  }
> = {
  WordPress: {
    iconUrl: wordpressIcon,
  },
  WiX: {
    iconUrl: wixIcon,
  },
  Shopify: {
    iconUrl: shopifyIcon,
  },
  Squarespace: {
    iconUrl: squarespaceIcon,
  },
  Weebly: {
    iconUrl: weeblyIcon,
  },
  Leadpages: {
    iconUrl: leadpagesIcon,
  },
  Duda: {
    iconUrl: dudaIcon,
  },
  'Google Tag Manager': {
    iconUrl: gtmIcon,
    shortName: 'GTM',
  },
  BigCommerce: {
    iconUrl: bigCommerce,
  },
  Other: {
    iconUrl: '',
  },
};

const domainIsProbablyNotOurFE = (domain: string) =>
  !domain.startsWith(window.location.origin) &&
  domain.indexOf('mobilemonkey.com') === -1;
const validWhiteListUrl = (url: string) =>
  url.length >= 4 && url.indexOf('.') > 0;

// tslint:disable-next-line: cyclomatic-complexity
export const CustomerChatInstallInformation = (props: Props) => {
  const {
    promoter,
    setShowGreenCueForActivate,
    usedInSlideout,
    hideWhitelist,
  } = props;
  const { id: botId, chatWidgetChannel } = useSelector(activeChatbotSelector);

  const promoterJSCode = usePromoterJSCode(promoter);
  const promoterJSCodes2Map = useCustomPromoterJSCodes(promoter);

  const domain = useSelector(domainSelector);
  const { domainsList = [], unavailable: domainsUnavailable } = domain;

  const [otherSiteType, setOtherSiteType] = useState('');
  const [siteUrl, setSiteUrl] = useState('');
  const [newWhitelistUrl, setNewWhitelistUrl] = useState('');
  const facebookPage = useSelector(activeFacebookPageSelector);
  const [facebookTokenStatus] = getConnectionStatus(facebookPage);
  const domainConfigurationBroken =
    facebookTokenStatus !== 'OK' || domainsUnavailable;

  useEffect(() => {
    if (promoter) {
      setOtherSiteType(
        isKnownSiteType(promoter.siteType) ? '' : promoter.siteType ?? '',
      );
    }
  }, [promoter]);
  useEffect(() => {
    if (domainsList) {
      setSiteUrl(s => s || domainsList.find(domainIsProbablyNotOurFE) || '');
    }
  }, [domainsList]);

  const promoterCRUDActions = usePromoterCRUDActions();
  const domainCRUDActions = useDomainCRUDActions();
  const chatbotCRUDActions = useChatbotCRUDActions();
  const { showSlideoutMenu } = useViewActions();
  const [isIntallGuaideSeen, setIsIntallGuaideSeen] = useState(false);

  const { isWebChatActivated } = useGreenCueMarkers();

  useEffect(
    () => setShowGreenCueForActivate?.(!!siteUrl && isIntallGuaideSeen),
    [isIntallGuaideSeen, siteUrl],
  );

  if (!promoter) {
    return null;
  }

  const updatePromoter = (updates: Partial<PromoterModel>) => {
    if (promoter?.id) {
      chatbotCRUDActions.updateSetupStepsRemaining(
        'chatWidgetUpdate',
        'completed',
      );

      // Update should really only need a partial.
      promoterCRUDActions.update(botId, {
        id: promoter.id,
        type: promoter.type,
        ...updates,
      } as any);
    }
  };

  const onSubmitDomainForm = (e: React.FormEvent) => {
    e.preventDefault();
    addDomain(newWhitelistUrl, false, true);
  };

  const addDomain = (
    url: string,
    updateSiteUrl: boolean,
    clearNewWhitelistUrl?: boolean,
  ) => {
    const parsedUrl = (() => {
      try {
        return new URL(url).origin;
      } catch {}
      try {
        return new URL(`https://${url}`).origin;
      } catch {}
    })();
    if (
      !parsedUrl ||
      !validWhiteListUrl(parsedUrl) ||
      domainsList.find(d => d === parsedUrl)
    ) {
      return;
    }
    const updatedDomainList = updateSiteUrl
      ? [parsedUrl, ...domainsList]
      : [...domainsList, parsedUrl];
    chatbotCRUDActions.updateSetupStepsRemaining(
      'chatWidgetUpdate',
      'completed',
    );
    domainCRUDActions.update({
      id: domain.id,
      connectedPageId: facebookPage?.id,
      domainsList: updatedDomainList,
    });
    if (updateSiteUrl) {
      setSiteUrl(parsedUrl);
    }
    if (clearNewWhitelistUrl) {
      setNewWhitelistUrl('');
    }
  };

  const removeDomain = (domainToRemove: string) => {
    chatbotCRUDActions.updateSetupStepsRemaining(
      'chatWidgetUpdate',
      'completed',
    );
    domainCRUDActions.update({
      id: domain.id,
      connectedPageId: domain.connectedPageId,
      domainsList: domainsList.filter(d => d !== domainToRemove),
    });
  };
  const { siteType, siteUsesGtm } = promoter;

  const siteTypeSet = typeof siteType === 'string';
  const selectedSiteType = siteUsesGtm
    ? ('Google Tag Manager' as KnownSiteType)
    : isKnownSiteType(siteType)
    ? siteType
    : 'Other';

  const widgetWithInvalidTokenClassName = domainConfigurationBroken
    ? 'customer-chat-installation-information__disabled'
    : '';
  const textInputClassName = !domainConfigurationBroken
    ? 'customer-chat-installation-information__text-input'
    : 'customer-chat-installation-information__text-input-disabled';

  return (
    <div className="customer-chat-installation-information">
      {domainConfigurationBroken &&
        chatWidgetChannel !== 'web_chat' &&
        promoter.kind !== PromoterTypes.GETEMAILS && (
          <div className="customer-chat-installation-information__invalid-token-error">
            <Alert
              type="error"
              showCloseIcon={false}
              css={{
                text: 'customer-chat-installation-information__alert-text',
              }}
            >
              Cannot update domain while Facebook token is invalid.{' '}
              <Link
                to={BotBuilderRoutes.settingsMaintenance.getLink({
                  chatbotId: botId,
                })}
                capitalize={false}
              >
                Click here to refresh Facebook token.{' '}
              </Link>
            </Alert>
          </div>
        )}
      {chatWidgetChannel !== 'web_chat' && !hideWhitelist && (
        <>
          <div className={widgetWithInvalidTokenClassName}>
            {!usedInSlideout && (
              <>
                <Heading
                  size={5}
                  className="customer-chat-installation-information__header"
                >
                  {!isWebChatActivated && !siteUrl && (
                    <GreenMarker className="customer-chat-installation-information__green-marker" />
                  )}
                  <div>What is your website URL?</div>
                </Heading>
                <TextInput
                  className={textInputClassName}
                  value={siteUrl}
                  onChange={e => setSiteUrl(e.target.value)}
                  onBlur={() => addDomain(siteUrl, true)}
                  disabled={domainConfigurationBroken}
                />
              </>
            )}
            <div className="customer-chat-installation-information__header">
              <TooltipHeading
                size={5}
                addMarginAbove
                tooltip="Your website domain must be whitelisted with FB in order to use the Messenger plugin.
                 See the list of whitelisted domains below."
                isBold={!usedInSlideout}
                tooltipColor={usedInSlideout ? '#262626' : ''}
              >
                FB Domain Whitelist
              </TooltipHeading>
            </div>
            <form
              className="customer-chat-installation-information__whitelist-domain-add-section"
              onSubmit={onSubmitDomainForm}
            >
              <TextInput
                className={textInputClassName}
                value={newWhitelistUrl}
                onChange={e => setNewWhitelistUrl(e.target.value)}
                disabled={domainConfigurationBroken}
                placeholder="Website URL"
              />
              <PrimaryButton
                className="customer-chat-installation-information__add-button"
                type="submit"
                disabled={!validWhiteListUrl(newWhitelistUrl)}
              >
                + Add
              </PrimaryButton>
            </form>
          </div>
          <div className="customer-chat-installation-information__whitelist-domain-list">
            {domainsList.filter(domainIsProbablyNotOurFE).map(d => (
              <div
                key={d}
                className="customer-chat-installation-information__whitelist-domain"
              >
                {d}{' '}
                <div
                  className="customer-chat-installation-information__whitelist-domain-remove-button"
                  onClick={() => removeDomain(d)}
                >
                  <i className="fas fa-times" />
                </div>
              </div>
            ))}
          </div>

          {!usedInSlideout && <Line />}
        </>
      )}
      <div
        className={
          usedInSlideout
            ? 'customer-chat-installation-information_guildeAndScriptWrapper'
            : ''
        }
      >
        <Heading
          size={5}
          isBold
          addMarginAbove={!usedInSlideout}
          className={classNames(
            'customer-chat-installation-information__header',
            {
              'customer-chat-installation-information__slideout-header':
                usedInSlideout,
            },
          )}
        >
          {!isWebChatActivated && !isIntallGuaideSeen && siteUrl && (
            <GreenMarker className="customer-chat-installation-information__green-marker" />
          )}
          <div>What website editor are you using?</div>
        </Heading>
        <div
          className={classNames(
            'customer-chat-installation-information__site-type-selector',
            { slideout: usedInSlideout },
          )}
        >
          {KNOWN_SITE_TYPES.map(siteType => (
            <div
              key={siteType}
              className={classNames(
                'customer-chat-installation-information__site-type',
                {
                  'customer-chat-installation-information__site-type--active':
                    !!promoter.siteType && selectedSiteType === siteType,
                },
              )}
              onClick={() => {
                updatePromoter({ siteType, siteUsesGtm: false });
                setIsIntallGuaideSeen(true);
                showSlideoutMenu('customerChatInstallationGuide', {
                  siteType,
                  promoterJSCode,
                  promoterJSCode2: promoterJSCodes2Map[siteType],
                  promoterType: promoter.type,
                });
              }}
            >
              {SITE_TYPE_DEFINITIONS[siteType].iconUrl ? (
                <img
                  src={SITE_TYPE_DEFINITIONS[siteType].iconUrl}
                  alt={siteType}
                />
              ) : (
                siteType
              )}
            </div>
          ))}
        </div>
        {siteTypeSet && selectedSiteType === 'Other' && (
          <TextInput
            className="customer-chat-installation-information__other-site-type-input"
            value={otherSiteType}
            onChange={e => setOtherSiteType(e.target.value)}
            onBlur={() =>
              updatePromoter({
                ...promoter,
                siteType: otherSiteType,
                siteUsesGtm: false,
              })
            }
            placeholder="Website Type"
          />
        )}

        {!!promoter.siteType && (
          <div className="customer-chat-installation-information__buttons">
            <PrimaryButton
              className="customer-chat-installation-information__installation-guide-button"
              onClick={() => {
                setIsIntallGuaideSeen(true);

                showSlideoutMenu('customerChatInstallationGuide', {
                  siteType: selectedSiteType,
                  promoterJSCode,
                  promoterJSCode2:
                    promoterJSCodes2Map[promoter.siteType as KnownSiteType],
                  promoterType: promoter.type,
                });
              }}
            >
              <span className="customer-chat-installation-information__buttons-installation-guide-text">
                See{' '}
                {SITE_TYPE_DEFINITIONS[selectedSiteType].shortName ||
                  selectedSiteType}{' '}
                Installation Guide
              </span>
            </PrimaryButton>
          </div>
        )}

        <Heading
          size={5}
          isBold
          addMarginAbove
          className={classNames(
            'customer-chat-installation-information__header',
            {
              'customer-chat-installation-information__slideout-header':
                usedInSlideout,
            },
          )}
        >
          Install code on Website
        </Heading>

        <div
          className={
            usedInSlideout ? 'customer-chat-installation-information__note' : ''
          }
        >
          Paste the code below immediately after the{' '}
          <strong>&lt;head&gt;</strong> tag on every page you want it to appear.
        </div>
        <textarea
          readOnly
          className={classNames(
            'customer-chat-installation-information__code',
            {
              'customer-chat-installation-information__code-small':
                promoter.type === PromoterTypes.GETEMAILS,
            },
          )}
          value={promoterJSCode}
          onFocus={e => e.target.select()}
        />

        <div className="customer-chat-installation-information__buttons">
          <CopyToClipboard
            text={promoterJSCode}
            onCopy={() =>
              notification.success({ message: 'Code copied successfully' })
            }
          >
            <PrimaryButton
              className={
                usedInSlideout
                  ? 'customer-chat-installation-information__buttons-copy-code'
                  : ''
              }
              size={usedInSlideout ? 'sm' : 'md'}
              leftIcon={usedInSlideout && <CopyOutlined color="white" />}
              css={{
                leftIcon:
                  'customer-chat-installation-information__copy-code-icon',
              }}
            >
              Copy Code
            </PrimaryButton>
          </CopyToClipboard>
        </div>
        {!usedInSlideout && (
          <div className="customer-chat-installation-information__channel-setting">
            <GreenCheckCircle />
            <div>{CHAT_WIDGET_CHANNEL_DESCRIPTIONS[chatWidgetChannel]}</div>
            {chatWidgetChannel === 'omnichat' && (
              <Link
                href="https://customers.ai/help/article/lceo0wtp0l-omni-chat"
                isBlank
              >
                <QuestionCircleIcon
                  className="customer-chat-installation-information__question-circle"
                  color={colors.accentBlue}
                />
              </Link>
            )}

            <Link
              to={BotBuilderRoutes.settingsCustomerChatWidget.getLink({
                chatbotId: botId,
              })}
            >
              Customize
            </Link>
          </div>
        )}
      </div>
    </div>
  );
};
